import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { trackNavClick } from '../../lib/analytics';
import { Link } from 'react-router-dom';

interface NavbarProps {
  scrolled?: boolean;
}

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Directory', href: '/directory' },
  { name: 'Jobs', href: '/jobs' },
  { name: 'Housing', href: '/housing' },
  { name: 'Community', href: '/community' },
  { name: 'Resources', href: '/resources' },
  { name: 'Free Guides', href: '/lead-magnets', highlight: true },
  { name: 'Blog', href: '/blog' }
];

export default function Navbar({ scrolled: initialScrolled }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (linkName: string, destination: string) => {
    trackNavClick(linkName, destination);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center gap-3 group"
            onClick={() => handleNavClick('Logo', '/')}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
              isScrolled ? 'bg-[#0A381F]' : 'bg-white'
            }`}>
              <i className={`ri-plane-line text-2xl transition-colors ${
                isScrolled ? 'text-white' : 'text-[#0A381F]'
              }`}></i>
            </div>
            <span className={`text-2xl font-bold transition-colors ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}>
              Ireland Bound
            </span>
          </a>

          {/* Desktop Navigation - Button Style */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="/directory"
              onClick={() => handleNavClick('Directory', '/directory')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Directory
            </a>
            <a
              href="/citizenship"
              onClick={() => handleNavClick('Citizenship', '/citizenship')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Citizenship
            </a>
            <a
              href="/housing"
              onClick={() => handleNavClick('Housing', '/housing')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Housing
            </a>
            <a
              href="/jobs"
              onClick={() => handleNavClick('Jobs', '/jobs')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Jobs
            </a>
            <a
              href="/community"
              onClick={() => handleNavClick('Community', '/community')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Community
            </a>
            <a
              href="/tools"
              onClick={() => handleNavClick('Tools', '/tools')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Tools
            </a>
            <a
              href="/blog"
              onClick={() => handleNavClick('Blog', '/blog')}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                isScrolled 
                  ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                  : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
              }`}
            >
              Blog
            </a>

            {user ? (
              <>
                <a
                  href="/dashboard"
                  onClick={() => handleNavClick('Dashboard', '/dashboard')}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    isScrolled 
                      ? 'bg-gray-100 text-gray-800 hover:bg-[#0A381F] hover:text-white' 
                      : 'bg-white/90 text-gray-800 hover:bg-white hover:shadow-md'
                  }`}
                >
                  Dashboard
                </a>
                <button
                  onClick={signOut}
                  className="bg-[#0A381F] text-white px-6 py-2 rounded-lg font-semibold hover:bg-emerald-800 transition-all whitespace-nowrap shadow-md"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <a
                href="/auth/admin-login"
                onClick={() => handleNavClick('Sign In', '/auth/admin-login')}
                className="bg-[#0A381F] text-white px-6 py-2 rounded-lg font-semibold hover:bg-emerald-800 transition-all whitespace-nowrap shadow-md"
              >
                Sign In
              </a>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center transition-colors ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            <i className={`text-2xl ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg animate-slide-in">
          <div className="px-4 py-6 space-y-3">
            <a
              href="/directory"
              onClick={() => handleNavClick('Directory Mobile', '/directory')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Directory
            </a>
            <a
              href="/citizenship"
              onClick={() => handleNavClick('Citizenship Mobile', '/citizenship')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Citizenship
            </a>
            <a
              href="/housing"
              onClick={() => handleNavClick('Housing Mobile', '/housing')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Housing
            </a>
            <a
              href="/jobs"
              onClick={() => handleNavClick('Jobs Mobile', '/jobs')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Jobs
            </a>
            <a
              href="/community"
              onClick={() => handleNavClick('Community Mobile', '/community')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Community
            </a>
            <a
              href="/tools"
              onClick={() => handleNavClick('Tools Mobile', '/tools')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Tools
            </a>
            <a
              href="/blog"
              onClick={() => handleNavClick('Blog Mobile', '/blog')}
              className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
            >
              Blog
            </a>
            {user ? (
              <>
                <a
                  href="/dashboard"
                  onClick={() => handleNavClick('Dashboard Mobile', '/dashboard')}
                  className="block bg-gray-100 text-gray-800 px-4 py-3 rounded-lg font-medium hover:bg-[#0A381F] hover:text-white transition-all whitespace-nowrap"
                >
                  Dashboard
                </a>
                <button
                  onClick={signOut}
                  className="w-full bg-[#0A381F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-800 transition-all whitespace-nowrap shadow-md"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <a
                href="/auth/admin-login"
                onClick={() => handleNavClick('Sign In Mobile', '/auth/admin-login')}
                className="block w-full bg-[#0A381F] text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-800 transition-all text-center whitespace-nowrap shadow-md"
              >
                Sign In
              </a>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
